<template>
    <div>
        <!-- SSR slot content: Blade HTML rendered for SEO, removed once Vue mounts -->
        <div v-if="showSeoContent"><slot></slot></div>

        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h2 style="font-size: 1.3rem; font-weight: 600; margin: 0;">Liquidity Pools</h2>
            <div class="d-flex gap-2 align-items-center">
                <wallet-button size="sm"></wallet-button>
            </div>
        </div>

        <!-- Filters -->
        <div class="compass-card mb-3">
            <div class="row g-2 align-items-end">
                <div class="col-md-3">
                    <label class="form-label small">Search</label>
                    <input
                        v-model="search"
                        type="text"
                        class="form-control form-control-sm"
                        placeholder="Search tokens, e.g. 'bonk sol'..."
                    >
                </div>
                <div class="col-md-2">
                    <label class="form-label small">DEX</label>
                    <select v-model="filterPlatform" class="form-select form-select-sm">
                        <option value="">All DEXes</option>
                        <option v-for="p in platforms" :key="p" :value="p">{{ p }}</option>
                    </select>
                </div>
                <div class="col-md-2">
                    <label class="form-label small">Pool Type</label>
                    <select v-model="filterType" class="form-select form-select-sm">
                        <option value="">All Types</option>
                        <option value="clmm">CLMM</option>
                        <option value="standard">Standard AMM</option>
                        <option value="cpamm">CP-AMM</option>
                        <option value="dlmm">DLMM</option>
                    </select>
                </div>
                <div class="col-md-2">
                    <label class="form-label small">Min TVL</label>
                    <select v-model="filterMinTvl" class="form-select form-select-sm">
                        <option value="0">Any</option>
                        <option value="1000">$1K+</option>
                        <option value="10000">$10K+</option>
                        <option value="100000">$100K+</option>
                        <option value="1000000">$1M+</option>
                    </select>
                </div>
                <div class="col-md-2">
                    <label class="form-label small">Sort By</label>
                    <select v-model="sortBy" class="form-select form-select-sm">
                        <option value="tvl">TVL</option>
                        <option value="volume_24h">Volume</option>
                        <option value="apr_24h">APR</option>
                        <option value="fees_24h">Fees</option>
                    </select>
                </div>
                <div class="col-md-1">
                    <button @click="toggleSort" class="btn btn-outline-secondary btn-sm w-100" :title="sortDir === 'desc' ? 'Descending' : 'Ascending'">
                        <i :class="sortDir === 'desc' ? 'fas fa-sort-amount-down' : 'fas fa-sort-amount-up'"></i>
                    </button>
                </div>
            </div>
            <!-- Farming filter hidden — no protocols currently report farm APY via SDK -->
            <div v-if="hasFarmingPools" class="mt-2 d-flex gap-2">
                <label class="form-check form-check-inline small">
                    <input v-model="farmingOnly" type="checkbox" class="form-check-input">
                    <span class="form-check-label">Farming rewards only</span>
                </label>
            </div>
        </div>

        <!-- Tabs -->
        <div class="analytics-tabs mb-3">
            <button :class="['tab-btn', activeTab === 'pools' ? 'active' : '']" @click="activeTab = 'pools'">
                All Pools <span v-if="filteredPools.length" class="badge bg-secondary ms-1">{{ filteredPools.length }}</span>
            </button>
            <button :class="['tab-btn', activeTab === 'positions' ? 'active' : '']" @click="activeTab = 'positions'">
                My Positions
            </button>
        </div>

        <!-- Pool Table -->
        <div v-if="activeTab === 'pools'">
            <div v-if="loading" class="text-center py-4">
                <div class="spinner-border text-purple" role="status"></div>
                <p class="mt-2 analytics-text-muted">Loading pools...</p>
            </div>

            <div v-else class="table-mobile-scroll">
                <table class="compass-table">
                    <thead>
                        <tr>
                            <th>Pool</th>
                            <th>DEX</th>
                            <th>Type</th>
                            <th class="text-end" style="cursor:pointer" @click="setSortBy('apr_24h')">
                                APR
                                <i v-if="sortBy==='apr_24h'" :class="sortDir==='desc' ? 'fas fa-caret-down' : 'fas fa-caret-up'"></i>
                            </th>
                            <th class="text-end" style="cursor:pointer" @click="setSortBy('tvl')">
                                TVL
                                <i v-if="sortBy==='tvl'" :class="sortDir==='desc' ? 'fas fa-caret-down' : 'fas fa-caret-up'"></i>
                            </th>
                            <th class="text-end mobile-hide" style="cursor:pointer" @click="setSortBy('volume_24h')">
                                24h Volume
                                <i v-if="sortBy==='volume_24h'" :class="sortDir==='desc' ? 'fas fa-caret-down' : 'fas fa-caret-up'"></i>
                            </th>
                            <th class="text-end mobile-hide" style="cursor:pointer" @click="setSortBy('fees_24h')">
                                24h Fees
                                <i v-if="sortBy==='fees_24h'" :class="sortDir==='desc' ? 'fas fa-caret-down' : 'fas fa-caret-up'"></i>
                            </th>
                            <th class="text-end mobile-hide">Fees/TVL</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="pool in paginatedPools" :key="pool.public_key" class="pool-row" @click="selectPool(pool)">
                            <td>
                                <div class="d-flex align-items-center gap-2">
                                    <div class="d-flex" style="min-width: 40px;">
                                        <img v-if="pool.token_a_logoURI" :src="pool.token_a_logoURI" :alt="pool.token_a_symbol" class="brround" style="width: 22px; height: 22px;" @error="onLogoError($event, pool, 'a')">
                                        <span v-if="!pool.token_a_logoURI" class="token-initial">{{ (pool.token_a_symbol || '?')[0] }}</span>
                                        <img v-if="pool.token_b_logoURI" :src="pool.token_b_logoURI" :alt="pool.token_b_symbol" class="brround" style="width: 22px; height: 22px; margin-left: -6px;" @error="onLogoError($event, pool, 'b')">
                                        <span v-if="!pool.token_b_logoURI" class="token-initial" style="margin-left: -6px;">{{ (pool.token_b_symbol || '?')[0] }}</span>
                                    </div>
                                    <div>
                                        <strong>{{ displaySymbol(pool.token_a_symbol, pool.token_a_mint) }}/{{ displaySymbol(pool.token_b_symbol, pool.token_b_mint) }}</strong>
                                        <div v-if="pool.fee_bps" class="small analytics-text-muted">{{ (pool.fee_bps / 100).toFixed(2) }}% fee</div>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <span class="compass-badge compass-badge--outline">{{ pool.platform }}</span>
                            </td>
                            <td>
                                <span :class="['compass-badge', pool.pool_type === 'clmm' || pool.pool_type === 'dlmm' ? 'compass-badge--purple' : 'compass-badge--outline']">
                                    {{ (pool.pool_type || 'standard').toUpperCase() }}
                                </span>
                            </td>
                            <td class="td-num text-end">
                                <span :class="(pool.total_apr || pool.internal_yield) > 10 ? 'text-success' : ''">
                                    {{ formatPercent(pool.total_apr || pool.internal_yield || pool.apr_24h) }}
                                </span>
                                <div v-if="pool.farm_apy && pool.farm_apy > 0" class="small analytics-text-muted">
                                    {{ formatPercent(pool.internal_yield || pool.apr_24h) }} + {{ formatPercent(pool.farm_apy) }} farm
                                </div>
                            </td>
                            <td class="td-num text-end">{{ formatUsd(pool.tvl) }}</td>
                            <td class="td-num text-end mobile-hide">{{ formatUsd(pool.volume_24h) }}</td>
                            <td class="td-num text-end mobile-hide">{{ formatUsd(pool.fees_24h) }}</td>
                            <td class="td-num text-end mobile-hide">
                                <span v-if="pool.fees_24h && pool.tvl">{{ formatPercent((pool.fees_24h / pool.tvl) * 365 * 100) }}</span>
                                <span v-else class="analytics-text-muted">—</span>
                            </td>
                            <td class="text-end">
                                <a v-if="pool.pool_link" :href="pool.pool_link" target="_blank" class="compass-btn compass-btn--sm compass-btn--outline" @click.stop>
                                    LP <i class="fas fa-external-link-alt" style="font-size: 0.7rem;"></i>
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
                <span class="analytics-text-muted small">
                    Showing {{ (page - 1) * perPage + 1 }}–{{ Math.min(page * perPage, filteredPools.length) }}
                    of {{ filteredPools.length }} pools
                </span>
                <div class="d-flex gap-1">
                    <button class="btn btn-outline-secondary btn-sm" :disabled="page <= 1" @click="page--">&laquo; Prev</button>
                    <button class="btn btn-outline-secondary btn-sm" :disabled="page >= totalPages" @click="page++">Next &raquo;</button>
                </div>
            </div>
        </div>

        <!-- Positions Tab -->
        <div v-if="activeTab === 'positions'">
            <lp-positions></lp-positions>
        </div>

        <!-- Pool Detail Drawer -->
        <div v-if="selectedPool" class="pool-detail-overlay" @click.self="selectedPool = null">
            <div class="pool-detail-drawer">
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <h4 class="mb-0">
                        <img v-if="selectedPool.token_a_logoURI" :src="selectedPool.token_a_logoURI" style="width: 24px; height: 24px; border-radius: 50%;" @error="onLogoError($event, selectedPool, 'a')">
                        <span v-if="!selectedPool.token_a_logoURI" class="token-initial">{{ (selectedPool.token_a_symbol || '?')[0] }}</span>
                        <img v-if="selectedPool.token_b_logoURI" :src="selectedPool.token_b_logoURI" style="width: 24px; height: 24px; border-radius: 50%; margin-left: -6px;" @error="onLogoError($event, selectedPool, 'b')">
                        <span v-if="!selectedPool.token_b_logoURI" class="token-initial" style="margin-left: -6px;">{{ (selectedPool.token_b_symbol || '?')[0] }}</span>
                        {{ selectedPool.name }}
                    </h4>
                    <button class="btn-close" @click="selectedPool = null"></button>
                </div>

                <!-- Pool Stats -->
                <div class="row mb-3">
                    <div class="col-4">
                        <div class="compass-stat-card compass-stat-card--compact">
                            <div class="stat-label">TVL</div>
                            <div class="stat-value font-mono-num">{{ formatUsd(selectedPool.tvl) }}</div>
                        </div>
                    </div>
                    <div class="col-4">
                        <div class="compass-stat-card compass-stat-card--compact">
                            <div class="stat-label">24h Volume</div>
                            <div class="stat-value font-mono-num">{{ formatUsd(selectedPool.volume_24h) }}</div>
                        </div>
                    </div>
                    <div class="col-4">
                        <div class="compass-stat-card compass-stat-card--compact">
                            <div class="stat-label">{{ selectedPool.farm_apy ? 'Total APR' : 'Fee APR' }}</div>
                            <div class="stat-value font-mono-num text-success">{{ formatPercent(selectedPool.total_apr || selectedPool.internal_yield || selectedPool.apr_24h) }}</div>
                        </div>
                    </div>
                </div>

                <!-- Pool Info -->
                <div class="compass-card mb-3">
                    <div class="row small">
                        <div class="col-6 mb-2">
                            <span class="analytics-text-muted">Platform</span><br>
                            <strong>{{ selectedPool.platform }}</strong>
                        </div>
                        <div class="col-6 mb-2">
                            <span class="analytics-text-muted">Pool Type</span><br>
                            <span class="compass-badge compass-badge--purple">{{ (selectedPool.pool_type || 'standard').toUpperCase() }}</span>
                        </div>
                        <div class="col-6 mb-2">
                            <span class="analytics-text-muted">LP Fee</span><br>
                            <strong>{{ selectedPool.fee_bps ? (selectedPool.fee_bps / 100).toFixed(2) + '%' : '—' }}</strong>
                        </div>
                        <div class="col-6 mb-2">
                            <span class="analytics-text-muted">Farm APR</span><br>
                            <strong :class="selectedPool.farm_apy ? 'text-success' : ''">
                                {{ selectedPool.farm_apy ? formatPercent(selectedPool.farm_apy) : '—' }}
                            </strong>
                        </div>
                        <div v-if="selectedPool.reward_tokens && selectedPool.reward_tokens.length" class="col-12 mb-2">
                            <span class="analytics-text-muted">Reward Tokens</span><br>
                            <span v-for="rt in selectedPool.reward_tokens" :key="rt.mint || rt.token" class="compass-badge compass-badge--outline me-1">
                                {{ rt.token || rt.symbol || rt.mint?.slice(0,6) }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Deposit Form (SDK-powered) -->
                <div class="compass-card compass-card--accent">
                    <h5 class="mb-3">Add Liquidity</h5>

                    <div v-if="!walletConnected" class="text-center py-3">
                        <p class="analytics-text-muted mb-2">Connect your wallet to provide liquidity</p>
                        <wallet-button></wallet-button>
                    </div>

                    <div v-else>
                        <!-- CLMM range selector -->
                        <div v-if="isClmm(selectedPool)" class="mb-3">
                            <label class="form-label small">Price Range</label>
                            <div class="row g-2">
                                <div class="col-6">
                                    <label class="form-label small analytics-text-muted">Min Price</label>
                                    <input v-model.number="depositForm.lowerPrice" type="number" step="any" class="form-control form-control-sm font-mono-num" placeholder="Min">
                                </div>
                                <div class="col-6">
                                    <label class="form-label small analytics-text-muted">Max Price</label>
                                    <input v-model.number="depositForm.upperPrice" type="number" step="any" class="form-control form-control-sm font-mono-num" placeholder="Max">
                                </div>
                            </div>
                            <div class="d-flex gap-1 mt-1">
                                <button v-for="pct in [5, 10, 25, 50]" :key="pct" class="btn btn-outline-secondary btn-sm flex-fill" @click="setRange(pct)">
                                    &plusmn;{{ pct }}%
                                </button>
                                <button class="btn btn-outline-secondary btn-sm flex-fill" @click="setFullRange">Full</button>
                            </div>
                        </div>

                        <!-- Token amounts -->
                        <div class="row g-2 mb-3">
                            <div class="col-6">
                                <label class="form-label small">{{ selectedPool.token_a_symbol || 'Token A' }}</label>
                                <input v-model.number="depositForm.amountA" type="number" step="any" min="0" class="form-control form-control-sm font-mono-num" placeholder="0.00">
                            </div>
                            <div class="col-6">
                                <label class="form-label small">{{ selectedPool.token_b_symbol || 'Token B' }}</label>
                                <input v-model.number="depositForm.amountB" type="number" step="any" min="0" class="form-control form-control-sm font-mono-num" placeholder="0.00">
                            </div>
                        </div>

                        <!-- Deposit Quote -->
                        <div v-if="depositQuote" class="compass-info-box compass-info-box--success mb-3 small">
                            <div class="d-flex justify-content-between">
                                <span>Estimated Value</span>
                                <strong class="font-mono-num">{{ formatUsd(depositQuote.estimatedValueUsd) }}</strong>
                            </div>
                            <div v-if="depositQuote.priceImpactPct != null" class="d-flex justify-content-between">
                                <span>Price Impact</span>
                                <span :class="depositQuote.priceImpactPct > 1 ? 'text-danger' : 'text-success'" class="font-mono-num">
                                    {{ depositQuote.priceImpactPct.toFixed(2) }}%
                                </span>
                            </div>
                        </div>

                        <!-- Action buttons -->
                        <div class="d-flex gap-2">
                            <button class="compass-btn compass-btn--primary flex-fill" :disabled="depositing" @click="getQuote">
                                {{ depositing ? 'Processing...' : 'Preview Deposit' }}
                            </button>
                            <button v-if="depositQuote" class="compass-btn compass-btn--primary flex-fill" :disabled="depositing" @click="executeDeposit">
                                {{ depositing ? 'Depositing...' : 'Deposit' }}
                            </button>
                        </div>

                        <!-- External link fallback -->
                        <div class="text-center mt-2">
                            <a v-if="selectedPool.pool_link" :href="selectedPool.pool_link" target="_blank" class="small analytics-text-muted">
                                Or deposit on {{ selectedPool.platform }} directly <i class="fas fa-external-link-alt"></i>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useSolSdk } from '../../composables/useSolSdk';
import { formatUSD, formatPercent as sharedFormatPercent, formatPubkey } from '../../composables/useFormatters';

export default {
    name: 'LiquidityPoolExplorer',
    props: {
        initialPools: { type: Array, default: () => [] },
        platforms: { type: Array, default: () => [] },
        initialStats: { type: Object, default: () => ({}) },
    },
    setup() {
        const { getSdk, isReady } = useSolSdk();
        return { getSdk, sdkReady: isReady };
    },
    data() {
        return {
            pools: this.initialPools || [],
            stats: this.initialStats || {},
            loading: false,
            search: '',
            filterPlatform: '',
            filterType: '',
            filterMinTvl: '0',
            sortBy: 'tvl',
            sortDir: 'desc',
            farmingOnly: false,
            activeTab: 'pools',
            page: 1,
            perPage: 50,
            selectedPool: null,
            depositForm: {
                amountA: null,
                amountB: null,
                lowerPrice: null,
                upperPrice: null,
            },
            depositQuote: null,
            depositing: false,
            showSeoContent: true,
            walletConnected: false,
        };
    },
    computed: {
        filteredPools() {
            let pools = this.pools;

            if (this.search) {
                // AND logic for space-separated terms: "bonk sol" matches pools with BOTH tokens
                const terms = this.search.toLowerCase().split(/\s+/).filter(Boolean);
                pools = pools.filter(p => {
                    const haystack = [
                        p.name, p.platform,
                        p.token_a_symbol, p.token_b_symbol,
                        p.token_a_name, p.token_b_name,
                        p.token_a_mint, p.token_b_mint,
                        p.public_key,
                    ].filter(Boolean).join(' ').toLowerCase();
                    return terms.every(term => haystack.includes(term));
                });
            }

            if (this.filterPlatform) {
                pools = pools.filter(p => p.platform === this.filterPlatform);
            }

            if (this.filterType) {
                pools = pools.filter(p => (p.pool_type || 'standard') === this.filterType);
            }

            if (parseFloat(this.filterMinTvl) > 0) {
                const minTvl = parseFloat(this.filterMinTvl);
                pools = pools.filter(p => (p.tvl || 0) >= minTvl);
            }

            if (this.farmingOnly) {
                pools = pools.filter(p => p.farm_apy && p.farm_apy > 0);
            }

            // Sort
            const dir = this.sortDir === 'desc' ? -1 : 1;
            const field = this.sortBy;
            pools = [...pools].sort((a, b) => {
                const av = a[field] ?? 0;
                const bv = b[field] ?? 0;
                return (av - bv) * dir;
            });

            return pools;
        },
        totalPages() {
            return Math.ceil(this.filteredPools.length / this.perPage);
        },
        hasFarmingPools() {
            return this.pools.some(p => p.farm_apy && p.farm_apy > 0);
        },
        paginatedPools() {
            const start = (this.page - 1) * this.perPage;
            return this.filteredPools.slice(start, start + this.perPage);
        },
    },
    watch: {
        search() { this.page = 1; },
        filterPlatform() { this.page = 1; },
        filterType() { this.page = 1; },
        filterMinTvl() { this.page = 1; },
        farmingOnly() { this.page = 1; },
    },
    mounted() {
        this.showSeoContent = false;
        this.walletConnected = !!window.wallet?.publicKey?.value;
        this.fetchPools();
        // Watch wallet connect/disconnect reactively
        if (window.wallet?.publicKey) {
            this.$watch(
                () => window.wallet.publicKey.value,
                (newVal) => { this.walletConnected = !!newVal; }
            );
        } else {
            setTimeout(() => {
                if (window.wallet?.publicKey) {
                    this.walletConnected = !!window.wallet.publicKey.value;
                    this.$watch(
                        () => window.wallet.publicKey.value,
                        (newVal) => { this.walletConnected = !!newVal; }
                    );
                }
            }, 500);
        }
    },
    methods: {
        async fetchPools() {
            this.loading = true;
            try {
                const params = new URLSearchParams();
                if (this.filterPlatform) params.set('protocol', this.filterPlatform);
                if (this.filterType) params.set('type', this.filterType);
                if (this.filterMinTvl && parseFloat(this.filterMinTvl) > 0) params.set('min_tvl', this.filterMinTvl);
                params.set('sort', this.sortBy);
                params.set('dir', this.sortDir);
                params.set('limit', '500');

                const { data } = await axios.get('/api/lp/pools?' + params.toString());
                if (data.pools && data.pools.length) {
                    this.pools = data.pools;
                }
                if (data.stats) {
                    this.stats = data.stats;
                }
            } catch (e) {
                console.warn('[LiquidityPoolExplorer] API fetch failed, using SSR data', e);
            } finally {
                this.loading = false;
            }
        },
        selectPool(pool) {
            this.selectedPool = pool;
            this.depositQuote = null;
            this.depositForm = { amountA: null, amountB: null, lowerPrice: null, upperPrice: null };
            // Listen for ESC to close drawer
            const onEsc = (e) => {
                if (e.key === 'Escape') {
                    this.selectedPool = null;
                    document.removeEventListener('keydown', onEsc);
                }
            };
            document.addEventListener('keydown', onEsc);
            this.$watch('selectedPool', (val) => {
                if (!val) document.removeEventListener('keydown', onEsc);
            });
        },
        isClmm(pool) {
            return ['clmm', 'dlmm'].includes(pool?.pool_type);
        },
        setRange(pct) {
            // Estimate current price from pool data
            const price = this.selectedPool?.price || 1;
            this.depositForm.lowerPrice = +(price * (1 - pct / 100)).toFixed(6);
            this.depositForm.upperPrice = +(price * (1 + pct / 100)).toFixed(6);
        },
        setFullRange() {
            this.depositForm.lowerPrice = 0;
            this.depositForm.upperPrice = null; // SDK interprets null as full range
        },
        async getQuote() {
            if (!this.sdkReady) return;
            this.depositing = true;
            try {
                const sdk = await this.getSdk();
                const params = {
                    poolId: this.selectedPool.public_key,
                    amountA: this.depositForm.amountA || undefined,
                    amountB: this.depositForm.amountB || undefined,
                };
                if (this.isClmm(this.selectedPool)) {
                    params.lowerPrice = this.depositForm.lowerPrice;
                    params.upperPrice = this.depositForm.upperPrice;
                }
                this.depositQuote = await sdk.lp.getDepositQuote(
                    window.wallet?.publicKey?.value?.toBase58(),
                    params,
                    this.mapProtocol(this.selectedPool.platform)
                );
            } catch (e) {
                console.error('[LiquidityPoolExplorer] Quote failed', e);
                alert('Failed to get deposit quote: ' + (e.message || e));
            } finally {
                this.depositing = false;
            }
        },
        async executeDeposit() {
            if (!this.sdkReady || !this.depositQuote) return;
            this.depositing = true;
            try {
                const sdk = await this.getSdk();
                const params = {
                    poolId: this.selectedPool.public_key,
                    amountA: this.depositForm.amountA || undefined,
                    amountB: this.depositForm.amountB || undefined,
                };
                if (this.isClmm(this.selectedPool)) {
                    params.lowerPrice = this.depositForm.lowerPrice;
                    params.upperPrice = this.depositForm.upperPrice;
                }
                const result = await sdk.lp.deposit(
                    window.wallet?.publicKey?.value?.toBase58(),
                    params,
                    this.mapProtocol(this.selectedPool.platform)
                );
                alert('Deposit successful! TX: ' + (result.signature || result.txId || 'confirmed'));
                this.selectedPool = null;
            } catch (e) {
                console.error('[LiquidityPoolExplorer] Deposit failed', e);
                alert('Deposit failed: ' + (e.message || e));
            } finally {
                this.depositing = false;
            }
        },
        mapProtocol(platform) {
            const map = {
                'Orca Whirlpool': 'orca',
                'Raydium': 'raydium',
                'Raydium CLMM': 'raydium',
                'Meteora': 'meteora',
                'Meteora DLMM': 'meteora',
                'Kamino': 'kamino',
            };
            return map[platform] || platform?.toLowerCase();
        },
        onLogoError(event, pool, side) {
            event.target.style.display = 'none';
            if (side === 'a') pool.token_a_logoURI = null;
            else pool.token_b_logoURI = null;
        },
        setSortBy(field) {
            if (this.sortBy === field) {
                this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
            } else {
                this.sortBy = field;
                this.sortDir = 'desc';
            }
        },
        toggleSort() {
            this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
        },
        formatUsd(val) {
            if (!val && val !== 0) return '—';
            return formatUSD(parseFloat(val));
        },
        formatPercent(val) {
            if (!val && val !== 0) return '—';
            return sharedFormatPercent(parseFloat(val));
        },
        shortMint(mint) {
            if (!mint) return '???';
            return mint.slice(0, 4) + '..' + mint.slice(-3);
        },
        displaySymbol(symbol, mint) {
            // Detect truncated mint addresses masquerading as symbols (base58-like, 6-10 chars, no spaces)
            if (symbol && /^[A-HJ-NP-Za-km-z1-9]{6,10}$/.test(symbol) && !/[aeiou]{2}/i.test(symbol)) {
                return this.shortMint(mint);
            }
            return symbol || this.shortMint(mint);
        },
    },
};
</script>

<style scoped>
.pool-row {
    cursor: pointer;
    transition: background-color 0.15s;
}
.pool-row:hover {
    background-color: rgba(139, 92, 246, 0.04);
}

.pool-detail-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1050;
    display: flex;
    justify-content: flex-end;
}

.pool-detail-drawer {
    width: 480px;
    max-width: 100vw;
    height: 100vh;
    overflow-y: auto;
    background: var(--bs-body-bg, #fff);
    padding: 1.5rem;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
    animation: slideIn 0.2s ease-out;
}

@keyframes slideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
}

.dark-mode .pool-detail-drawer {
    background: #1a1a2e;
}

.dark-mode .pool-row:hover {
    background-color: rgba(139, 92, 246, 0.08);
}

.token-initial {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    flex-shrink: 0;
}

@media (max-width: 768px) {
    .pool-detail-drawer {
        width: 100vw;
    }
}
</style>
